import { falkorDBService } from './services/falkordb.service';
import {
  MCPResponse,
  MCPProviderMetadata
} from './models/mcp.types';
import { z } from "zod";
import {
  McpServer,
  ResourceTemplate,
} from "@modelcontextprotocol/sdk/server/mcp.js";
import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";
import { config } from './config';

// Create an MCP server
const server = new McpServer({
  name: "EE-GRAPH-MCP",
  version: "1.0.0",
  description: "Evolution Engineering Graph MCP Server",
});

// Add a tools

// Read Me for AI Agent
server.tool("read-me", {}, async (args, extra) => {
  const readMe= {
    provider: 'EE Graph MCP Server',
    version: '1.0.0',
    instructions: "First, test if your API key is valid. If the API key is valid, then get the ontology metadata and instructions. If the API key is not valid, then return an error message. Metadata and instructions will guide you where to find releavant information, how they are connected and how to construct your query to get the information you need. You can get full ontology model using tool get-ontology-model. Use tool query-ontology-semantics to query the ontology semantics and understand the ontology model. Use tool query-knowledge-graph to query real data stored in the knowledge graph.",
  };
  return {
    content: [
      {
        type: "text",
        text: JSON.stringify(readMe),
      },
    ],
  };
});

// Check if the API key is valid
server.tool("is-api-key-valid", { apiKey: z.string().default(config.mcp.apiKey).optional() }, async (args: { apiKey?: string }, extra) => {
  const startTime = Date.now();
  if (args.apiKey === null || args.apiKey === undefined || args.apiKey === "*") {
    return {
      content: [
        { type: "text", text: JSON.stringify({ error: "API key is required" }) }
      ],
      isError: true 
    };
  }

  const result = await falkorDBService.verifyApiKey(args.apiKey);
  if (!result.valid) {
    return {
      content: [
        { type: "text", text: JSON.stringify({ error: result.message }) }
      ],
      isError: true   
    };
  }

  const queryTime = Date.now() - startTime;
  const formattedResult: MCPResponse = {
    data: `API key ${args.apiKey} is valid`,
    metadata: {
      timestamp: new Date().toISOString(),
      queryTime,
      provider: 'EE MCP Server',
      source: 'EE Semantic Graphs'
    }
  };

  return {
    content: [
      {
        type: "text",
        text: JSON.stringify(formattedResult),
      },
    ],
  };
});

// Get the Ontology Metadata and Instructions
server.tool("get-ontology-metadata-and-instructions", {}, async (args, extra) => {
  const response = await falkorDBService.getOntologyMetadata(
    config.mcp.apiKey
  );
  const metadata: MCPProviderMetadata = {
    provider: 'EE Graph MCP Server',
    version: '1.0.0',
    instructions: response,
    capabilities: [
      'get.ontology.instructions',
      'get.ontology.metadata',
      'get.ontology.model',
      'query.ontology.semantics',
      'query.knowledge.graph',
    ],
    graphTypes: ['property', 'directed'],
    queryLanguages: ['cypher'],
  };
  return {
    content: [
      {
        type: "text",
        text: JSON.stringify(metadata),
      },
    ],
  };
});

// Get the Ontology Model
server.tool("get-ontology-model", { apiKey: z.string().default(config.mcp.apiKey).optional() }, async (args, extra) => {
  if (args.apiKey === null || args.apiKey === undefined || args.apiKey === "*") {
    return {
      content: [
        {
          type: "text",
          text: JSON.stringify({ error: "API key is required" }),
        },
      ],
      isError: true
    };
  }

  try {
    const startTime = Date.now();

    // Execute the query using FalkorDB service 
    const result = await falkorDBService.getOntology(
      args.apiKey
    );
    const queryTime = Date.now() - startTime;

    // Format the result according to MCP standards
    const formattedResult: MCPResponse = {
      data: result,
      metadata: {
        timestamp: new Date().toISOString(),
        queryTime,
        provider: 'EE MCP Server',
        source: 'EE Semantic Graphs'
      }
    };

    return {
      content: [
        {
          type: "text",
          text: JSON.stringify(formattedResult),
        },
      ],
      _meta: {
        timestamp: new Date().toISOString(),
        queryTime
      }
    };
  } catch (error: any) {
    return {
      content: [
        {
          type: "text",
          text: JSON.stringify({ error: error.message }),
        },
      ],
      _meta: {
        timestamp: new Date().toISOString()
      },
      isError: true
    };
  }
});

// Query the Model Graph
server.tool("query-ontology-semantics", { query: z.string(), apiKey: z.string().default(config.mcp.apiKey).optional() }, async (args, extra) => {
  if (args.apiKey === null || args.apiKey === undefined || args.apiKey === "*") {
    return {
      content: [
        {
          type: "text",
          text: JSON.stringify({ error: "API key is required" }),
        },
      ],
      isError: true
    };
  }

  try {
    const startTime = Date.now();

    // Execute the query using FalkorDB service
    const response = await falkorDBService.executeOntologyGraphQuery(
      args.query,
      args.apiKey
    );
    const queryTime = Date.now() - startTime;

    // Format the result according to MCP standards
    const formattedResult: MCPResponse = {
      data: response.result,
      metadata: {
        timestamp: new Date().toISOString(),
        queryTime,
        provider: 'EE MCP Server',
        source: 'EE Semantic Graphs'
      }
    };

    return {
      content: [
        {
          type: "text",
          text: JSON.stringify(formattedResult),
        },
      ],
      _meta: {
        timestamp: new Date().toISOString(),
        queryTime
      }
    };
  } catch (error: any) {
    return {
      content: [
        {
          type: "text",
          text: JSON.stringify({ error: error.message }),
        },
      ],
      _meta: {
        timestamp: new Date().toISOString()
      },
      isError: true
    };
  }
});

// Query the Ontology Graph
server.tool("query-knowledge-graph", { query: z.string(), apiKey: z.string().default(config.mcp.apiKey).optional() }, async (args, extra) => {
  if (args.apiKey === null || args.apiKey === undefined || args.apiKey === "*") {
    return {
      content: [
        {
          type: "text",
          text: JSON.stringify({ error: "API key is required" }),
        },
      ],
      isError: true
    };
  }

  try {
    const startTime = Date.now();

    // Execute the query using FalkorDB service
    const response = await falkorDBService.executeGraphQuery(
      args.query,
      args.apiKey
    );
    const queryTime = Date.now() - startTime;

    // Format the result according to MCP standards
    const formattedResult: MCPResponse = {
      data: response.result,
      metadata: {
        timestamp: new Date().toISOString(),
        queryTime,
        provider: 'EE MCP Server',
        source: 'EE Semantic Graphs'
      }
    };

    return {
      content: [
        {
          type: "text",
          text: JSON.stringify(formattedResult),
        },
      ],
      _meta: {
        timestamp: new Date().toISOString(),
        queryTime
      }
    };
  } catch (error: any) {
    return {
      content: [
        {
          type: "text",
          text: JSON.stringify({ error: error.message }),
        },
      ],
      _meta: {
        timestamp: new Date().toISOString()
      },
      isError: true
    };
  }
});

// Start receiving messages on stdin and sending messages on stdout
async function main() {
  const transport = new StdioServerTransport();
  await server.connect(transport);
  console.error("EE MCP Server running on stdio");
}

process.on('SIGTERM', async () => {
  console.log('SIGTERM received. Shutting down gracefully...');
  // Close database connections
  await falkorDBService.close();
  process.exit(0);
});

process.on('SIGINT', async () => {
  console.log('SIGINT received. Shutting down gracefully...');
  // Close database connections
  await falkorDBService.close();
  process.exit(0);
});

main().catch((error) => {
  console.error("Fatal error in main():", error);
  process.exit(1);
});

